import { useId, useRef, useState, type ReactNode } from "react";
import {
  Bold,
  Code2,
  Eye,
  Heading2,
  Italic,
  Link,
  List,
  ListOrdered,
  Pencil,
  Plus,
  Quote,
} from "lucide-react";
import { Markdown } from "./markdown";
import { Dialog } from "./dialog";
import { cn } from "@hey404/ui";
import { titleClass } from "./titles";

// Single field treatment across the app, from the V30 design system: cream
// fill, 2px ink border, control radius, and a pink ring plus 3px border when
// `aria-invalid` is set, so error state can't drift from the aria wiring.
export const inputClass =
  "border-ink bg-paper text-ink border-chip rounded-control placeholder:text-ink-soft aria-invalid:border-surface aria-invalid:shadow-invalid disabled:bg-paper-dim mt-1 min-h-11 w-full px-3 py-2.5 text-base leading-normal normal-case disabled:cursor-not-allowed";
export const labelClass = "font-mono text-sm font-bold";
// Inline error under a field: danger bar on the left, ink text (V30
// `h4-field__error`). The colour is never the only signal.
export const fieldErrorClass =
  "border-l-danger bg-paper text-ink border-l-4 px-3 py-2 font-mono text-sm font-bold";
// Short editorial label above a title (V30 `h4-kicker`).
export const kickerClass =
  "font-mono text-sm font-bold uppercase leading-snug tracking-[0.08em]";

// Semantic button system on the V30 button: ink border, control radius, small
// hard shadow, regular-weight body type, and a grape fill on hover/focus
// instead of movement. Callers still add their own padding and type size so
// existing layouts keep working.
const btnBase =
  "border-ink text-ink border-surface rounded-control shadow-brutal hover:bg-grape focus-visible:bg-grape disabled:bg-paper-dim disabled:text-ink-soft inline-flex min-h-11 items-center justify-center gap-2 text-center leading-tight transition-colors duration-[120ms] disabled:cursor-not-allowed disabled:shadow-none";
export const btnPrimary = `${btnBase} bg-bubble`;
export const btnSecondary = `${btnBase} bg-paper`;
// V30 `h4-button--green`: confirmations (approve, complete, save) that
// existing screens already colour-code green.
export const btnAccent = `${btnBase} bg-acid`;
// Reversible but consequential actions (suspend): lemon, distinct from the
// pink primary.
export const btnDestructive = `${btnBase} bg-warning`;
// Irreversible actions (delete): ink with cream text, turning pink on hover.
// Red isn't in the brand palette — V30 keeps it for the error bar only — so
// danger is carried by the heaviest colour plus explicit wording.
export const btnDanger = cn(
  btnBase,
  "bg-ink text-paper hover:bg-bubble hover:text-ink focus-visible:bg-bubble focus-visible:text-ink",
);
export const btnGhost =
  "border-ink text-ink border-chip rounded-control hover:bg-grape inline-flex min-h-11 items-center justify-center gap-2 bg-transparent transition-colors duration-[120ms] disabled:cursor-not-allowed disabled:opacity-40";

// Form-level error inside the admin: ink frame with the danger bar, ink text
// (same callout as the rest of the platform; never a pink fill).
export const adminErrorClass =
  "border-ink border-l-danger bg-paper border-surface border-l-8 p-4 text-sm";

/**
 * Section title inside an admin screen (the page itself carries the
 * PageHeader): optional kicker with the pink square, display title, one-line
 * description and optional actions on the right.
 */
export function AdminHeading({
  kicker,
  title,
  description,
  actions,
}: {
  kicker?: string;
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className="flex flex-wrap items-end justify-between gap-4">
      <div className="min-w-0">
        {kicker && (
          <p className="mb-2 flex items-center gap-2.5 font-mono text-xs font-bold uppercase tracking-[0.06em]">
            <span
              aria-hidden="true"
              className="border-ink bg-bubble border-chip inline-block size-2.5 rotate-[-15deg]"
            />
            {kicker}
          </p>
        )}
        <h2 className={titleClass.section}>{title}</h2>
        {description && (
          <p className="text-ink-soft mt-2 max-w-2xl">{description}</p>
        )}
      </div>
      {actions && <div className="flex flex-wrap gap-3">{actions}</div>}
    </header>
  );
}

export function MarkdownField({
  name,
  defaultValue,
  label,
  rows = 6,
}: {
  name: string;
  defaultValue: string;
  label: string;
  rows?: number;
}) {
  const [value, setValue] = useState(defaultValue);
  const [preview, setPreview] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fieldId = useId();
  const format = (prefix: string, suffix = "", placeholder = "texto") => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selected = value.slice(start, end) || placeholder;
    const insertion = `${prefix}${selected}${suffix}`;
    setValue(`${value.slice(0, start)}${insertion}${value.slice(end)}`);
    window.requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(
        start + prefix.length,
        start + prefix.length + selected.length,
      );
    });
  };
  const tools = [
    { label: "Título", icon: Heading2, run: () => format("## ", "", "Título") },
    { label: "Negrita", icon: Bold, run: () => format("**", "**") },
    { label: "Cursiva", icon: Italic, run: () => format("_", "_") },
    { label: "Lista", icon: List, run: () => format("- ", "", "Elemento") },
    {
      label: "Lista numerada",
      icon: ListOrdered,
      run: () => format("1. ", "", "Elemento"),
    },
    { label: "Cita", icon: Quote, run: () => format("> ", "", "Cita") },
    { label: "Código", icon: Code2, run: () => format("`", "`", "código") },
    {
      label: "Enlace",
      icon: Link,
      run: () => format("[", "](https://)", "texto del enlace"),
    },
  ];
  return (
    <div className="sm:col-span-2">
      <label htmlFor={fieldId} className={labelClass}>
        {label}
      </label>
      <div className="border-ink bg-paper border-chip mt-1 normal-case">
        <div className="border-ink border-b-(length:--border-chip) flex flex-wrap items-center justify-between gap-2 p-2">
          <div className="flex flex-wrap gap-1">
            {tools.map(({ label: toolLabel, icon: Icon, run }) => (
              <button
                key={toolLabel}
                type="button"
                onClick={run}
                disabled={preview}
                title={toolLabel}
                aria-label={toolLabel}
                className="border-ink bg-bone disabled:text-ink-soft border px-2 py-1.5 disabled:cursor-not-allowed"
              >
                <Icon size={15} />
              </button>
            ))}
          </div>
          <div className="border-ink border-chip flex font-mono text-xs font-bold uppercase">
            <button
              type="button"
              onClick={() => setPreview(false)}
              className={`flex items-center gap-1 px-2 py-1.5 ${preview ? "bg-paper" : "bg-acid"}`}
            >
              <Pencil size={12} /> Escribir
            </button>
            <button
              type="button"
              onClick={() => setPreview(true)}
              className={`border-ink border-l-(length:--border-chip) flex items-center gap-1 px-2 py-1.5 ${preview ? "bg-acid" : "bg-paper"}`}
            >
              <Eye size={12} /> Vista previa
            </button>
          </div>
        </div>
        <textarea
          ref={textareaRef}
          id={fieldId}
          name={name}
          value={value}
          onChange={(event) => setValue(event.target.value)}
          rows={rows}
          hidden={preview}
          className="bg-paper w-full resize-y px-3 py-3 font-mono text-sm outline-none"
          placeholder="Puedes usar títulos, listas, links, tablas y código con Markdown."
        />
        {preview && (
          <Markdown className="min-h-40 p-4 text-sm">
            {value || "_La vista previa aparecerá aquí._"}
          </Markdown>
        )}
      </div>
    </div>
  );
}

export function CreateModal({
  open,
  title,
  description,
  onClose,
  children,
}: {
  open: boolean;
  title: string;
  description: string;
  onClose: () => void;
  children: ReactNode;
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      kicker="Crear"
      title={title}
      description={description}
      icon={Plus}
      tone="acid"
      size="lg"
    >
      {children}
    </Dialog>
  );
}
